/** The clicker reader on the Teacher Computer app (STORY-086 M2): where the adapter runs, which
 * live lesson its presses go to, and the ids the hub heard that this course does not map. Shown
 * inside the course's clicker section on a local host only: the reader is a thread in this
 * computer's sidecar, and a server has none. `onMap` hands an unknown id to the mapping form
 * (`DeviceMapPanel`'s `initialKey`, which the caller remounts). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Button } from "../ui";
import { attachClickers, getClickerStatus, listLiveSessions, type ClickerStatus } from "./api";

const REFUSALS = new Set(["seat_required", "student_row"]);

export function ClickerReader({ courseId, onMap }: { courseId: string; onMap: (key: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // the hub reports every 10 s; a poll at 5 s shows an unplugged receiver within the period
  const status = useQuery({ queryKey: ["clickerStatus"], queryFn: getClickerStatus, refetchInterval: 5_000 });
  const live = useQuery({ queryKey: ["sessions", courseId, "live"], queryFn: () => listLiveSessions(courseId), refetchInterval: 15_000 });
  const attach = useMutation({
    mutationFn: (sessionId: string | null) => attachClickers(sessionId),
    onSuccess: (s: ClickerStatus) => qc.setQueryData(["clickerStatus"], s),
  });

  const s = status.data;
  return (
    <section aria-labelledby="clicker-reader" className="mt-4 border-t border-[color:var(--line)] pt-4">
      <h3 id="clicker-reader" className="font-medium">{t("course.devices.reader.title")}</h3>
      {status.isLoading && <p className="text-sm text-[color:var(--ink-2)]">{t("course.devices.loading")}</p>}
      {status.isError && <p role="alert" className="text-sm">{t("course.devices.reader.loadError")}</p>}
      {s && !s.reader && <p className="measure mt-1 text-sm text-[color:var(--ink-2)]">{t("course.devices.reader.noKeys")}</p>}
      {s?.reader && (
        <div className="mt-1 flex flex-col gap-3 text-sm">
          <p role="status" data-clicker-dongle={s.dongle}>
            {t(`course.devices.reader.dongle.${s.dongle}`)}
          </p>
          <Attach
            attached={s.attached}
            sessions={(live.data ?? []).map((x) => ({ id: x.session_id, code: x.join_code }))}
            pending={attach.isPending}
            onAttach={(id) => attach.mutate(id)}
          />
          {attach.isError && <p role="alert">{t("course.devices.reader.attachError")}</p>}
          {Object.keys(s.refused).length > 0 && (
            <ul className="flex flex-col gap-1">
              {Object.entries(s.refused).map(([key, code]) => (
                <li key={key}>{t(`course.devices.reader.refused.${REFUSALS.has(code) ? code : "other"}`, { key, code })}</li>
              ))}
            </ul>
          )}
          {s.unknown_ids.length > 0 && (
            <div>
              <h4 className="font-medium">{t("course.devices.reader.unknownTitle")}</h4>
              <p className="measure text-[color:var(--ink-2)]">{t("course.devices.reader.unknownHint")}</p>
              <ul className="mt-2 flex flex-col gap-2">
                {s.unknown_ids.map((key) => (
                  <li key={key} className="flex flex-wrap items-center gap-3">
                    <code id={`unknown-${key}`} className="num">{key}</code>
                    <Button variant="secondary" onClick={() => onMap(key)} aria-describedby={`unknown-${key}`}>
                      {t("course.devices.reader.map")}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Attach({
  attached,
  sessions,
  pending,
  onAttach,
}: {
  attached: string | null;
  sessions: { id: string; code: string }[];
  pending: boolean;
  onAttach: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const here = sessions.find((x) => x.id === attached);
  if (here) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p>{t("course.devices.reader.attached", { code: here.code })}</p>
        <Button variant="tertiary" loading={pending} onClick={() => onAttach(null)}>
          {t("course.devices.reader.detach")}
        </Button>
      </div>
    );
  }
  if (sessions.length === 0) {
    return <p className="text-[color:var(--ink-2)]">{attached ? t("course.devices.reader.elsewhere") : t("course.devices.reader.noLesson")}</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      {attached && <p className="text-[color:var(--ink-2)]">{t("course.devices.reader.elsewhere")}</p>}
      <div className="flex flex-wrap gap-2">
        {sessions.map((x) => (
          <Button key={x.id} variant="secondary" loading={pending} onClick={() => onAttach(x.id)}>
            {t("course.devices.reader.attach", { code: x.code })}
          </Button>
        ))}
      </div>
    </div>
  );
}
